import { useCallback, useMemo, useState } from "react";

import { useGetOrderStats, useGetOrders } from "@/api";
import type { GetOrdersParams } from "@/api/schemas/getOrdersParams";
import { GetOrdersOrder } from "@/api/schemas/getOrdersOrder";
import { GetOrdersSortBy } from "@/api/schemas/getOrdersSortBy";
import type { GetOrdersStatus } from "@/api/schemas/getOrdersStatus";
import { parseOrderListResponse } from "@/lib/validation/dashboard.schemas";
import { useSessionStore } from "@/stores/session.store";

const PER_PAGE = 10;

export function useOrdersList() {
  const activeStoreId = useSessionStore((state) => state.activeStoreId);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<GetOrdersStatus | undefined>();

  const params = useMemo((): GetOrdersParams | undefined => {
    if (activeStoreId === null) return undefined;

    return {
      store_id: activeStoreId,
      page,
      per_page: PER_PAGE,
      sort_by: GetOrdersSortBy.createdAt,
      order: GetOrdersOrder.DESC,
      search: search.trim() || undefined,
      status: statusFilter,
    };
  }, [activeStoreId, page, search, statusFilter]);

  const { data, isLoading, isError } = useGetOrders(params, {
    query: {
      enabled: activeStoreId !== null,
      select: (response) => parseOrderListResponse(response.data),
    },
  });

  const { data: statsData, isLoading: isStatsLoading } = useGetOrderStats(
    activeStoreId !== null ? { store_id: activeStoreId } : undefined,
    { query: { enabled: activeStoreId !== null } },
  );

  const orders = data?.data ?? [];
  const meta = data?.meta;
  const stats = statsData?.data?.data;

  const handleStatusFilter = useCallback((status: GetOrdersStatus | undefined) => {
    setStatusFilter(status);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  return {
    orders,
    meta,
    stats,
    page,
    setPage,
    search,
    setSearch: handleSearchChange,
    statusFilter,
    setStatusFilter: handleStatusFilter,
    isLoading,
    isStatsLoading,
    isError,
    isEmpty:
      !isLoading &&
      !isStatsLoading &&
      (stats?.total_count ?? meta?.total ?? 0) === 0,
  };
}
